import { useState, MouseEvent, useEffect, useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { getCatalogKnmReasonsCanceled } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './AdminEditForm.module.scss';
import { adminCreateCatalogTypes, adminGetCatalogTypes, adminUpdateCatalogTypes } from '../actions/adminAction';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { IKnmReasonCanceled } from '../../../APIPackage/CatalogsModule/interfaces/IKnmReasonCanceled';

type KnmReasonCanceledType = Omit<IKnmReasonCanceled, 'id'>;

interface IKnmReasonCanceledEditFormProps {
  onBack: () => void;
}

interface IKnmReasonCanceledEditFormStoreProps {
  knmReasonsCanceled: IKnmReasonCanceled[];
}

const KnmReasonCanceledEditFormWithoutStore = ({
  onBack,
  knmReasonsCanceled,
}: IKnmReasonCanceledEditFormProps & IKnmReasonCanceledEditFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedKnmAddressType, setSelectedKnmAddressType] = useState<IKnmReasonCanceled | undefined>();
  const [isNew, setIsNew] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const onRowClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { id } = event.currentTarget.dataset;
      const knmAddressType = knmReasonsCanceled.find(k => k.id.toString() === id);

      setSelectedKnmAddressType(prev => (prev?.id === knmAddressType?.id ? undefined : knmAddressType));
    },
    [knmReasonsCanceled],
  );

  useEffect(() => {
    dispatch(adminGetCatalogTypes(CommonCatalogsNamesEnum.KNM_REASONS_CANCELED));
  }, [dispatch]);

  const onCreate = useCallback(() => {
    setIsNew(true);
    setSelectedKnmAddressType({
      id: -1,
      code: '',
      name: '',
    });
    setIsFormOpen(true);
  }, []);

  const onEdit = useCallback(() => setIsFormOpen(true), []);

  const onCancel = useCallback(() => {
    setIsNew(false);
    setSelectedKnmAddressType(undefined);
    setIsFormOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: KnmReasonCanceledType) => {
      if (isNew) {
        dispatch(adminCreateCatalogTypes({ data: values, catalogName: CommonCatalogsNamesEnum.KNM_REASONS_CANCELED }));
      } else {
        dispatch(
          adminUpdateCatalogTypes({
            data: { id: selectedKnmAddressType!.id, ...values },
            catalogName: CommonCatalogsNamesEnum.KNM_REASONS_CANCELED,
          }),
        );
      }
      onCancel();
    },
    [dispatch, isNew, onCancel, selectedKnmAddressType],
  );

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<KnmReasonCanceledType>) => (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Название" color="black">
            <TextInputField name="name" validate={makeRequired('Поле обязательно')} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Код" color="black">
            <TextInputField name="code" disabled={!isNew} />
          </FieldBlock>
        </Row>
        <div style={{ display: 'flex', gap: '12px', margin: '12px 0' }}>
          <CheckboxField type="checkbox" id="enabled" name="enabled" label="Активен" />
          <CheckboxField type="checkbox" id="pm" name="pm" label="Применимо к ПМ" />
        </div>
        <ModalActions placement="left" padding="none">
          <Button color="primary" text="Сохранить" id="saveButton" type="submit" variant="contained" />
          <Button color="default" text="Отмена" variant="contained" onClick={onCancel} />
        </ModalActions>
      </form>
    ),
    [isNew, onCancel],
  );

  if (isFormOpen) {
    return (
      <>
        <Form<KnmReasonCanceledType> onSubmit={onSubmit} initialValues={selectedKnmAddressType} render={renderForm} />
      </>
    );
  }

  return (
    <div>
      <div className={styles.Table}>
        <span className={styles.Header}>Основания отмены проверки</span>
        {knmReasonsCanceled &&
          knmReasonsCanceled.map((knmInspType: IKnmReasonCanceled) => (
            <button
              key={knmInspType.id}
              className={selectedKnmAddressType?.id === knmInspType.id ? styles.DataRowSelected : styles.DataRow}
              data-id={knmInspType.id}
              onClick={onRowClick}
              type="button"
            >
              {knmInspType.name}
            </button>
          ))}
      </div>
      <div className={styles.ControlButtons}>
        <Button
          className={styles.Button}
          color="default"
          type="button"
          variant="contained"
          onClick={onBack}
          text="Назад"
        />
        <Button
          className={styles.Button}
          color="primary"
          type="button"
          variant="contained"
          onClick={onCreate}
          text="Создать"
          id="createButton"
        />
        {selectedKnmAddressType && (
          <Button
            className={styles.Button}
            color="default"
            type="button"
            variant="contained"
            onClick={onEdit}
            text="Редактировать"
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmReasonCanceledEditFormStoreProps => ({
  knmReasonsCanceled: getCatalogKnmReasonsCanceled(state),
});

export const KnmReasonCanceledEditForm = connect(mapStateToProps)(KnmReasonCanceledEditFormWithoutStore);
